import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import {
	AppBar,
	Button,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Popover,
	Theme,
	Tooltip,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { percent } from 'csx'
import { reaction, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import React, { useEffect, useMemo, useState } from 'react'
import { toastService } from '../../services/notifications/ToastService'
import { DetailFormWithData } from '../../services/offline/FormPackageHandling'
import { useFormPackageControlApi } from './FormPackageControlApi'

type PackageFormSwapperProps = {
	forms: DetailFormWithData[]
	onSelectForm: (index: number) => number
	isCurrentFormValid: (index: number) => boolean
	isWizardMode: boolean
}

/**
 * This component is overlayed on top of the form viewer - it includes the app bar that displays the
 * form name and lets the user go to previous/next form using the arrow buttons or select a form from
 * the popover, as well as arrow icons that are on the left/right sides of the form that let the user
 * go to the previous/next form. I created this component to overlay on top of the form viewer in the
 * Package Host b/c we can't store the selectedIndex in state there, b/c it'll force all the forms to
 * re-render every time we swap to a different form, which is bad! We do have a ref in the Package Host
 * where we keep track of what form we're on, but changing that doesn't re-render the child components,
 * which we need to update the form name/disabled arrow icons.
 * So, all this to say, we needed a component where the selected form index would be shared between the
 * app bar and the side arrows, without updating PackageHost's state. The solution is a component that
 * is on top of the form viewer! Added some styling to make it look like part of the form viewer. -Caroline
 */
export const PackageFormSwapper = observer(
	React.forwardRef(
		(
			props: PackageFormSwapperProps,
			ref: React.ForwardedRef<HTMLDivElement>,
		) => {
			const styles = useStyles()
			const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

			const formPackageControlApi = useFormPackageControlApi()

			const forms = useMemo(() =>
				props.forms.sort((a, b) => a.ordinalPosition - b.ordinalPosition)
				, [props.forms])
			const formNames = useMemo(() =>
				forms.map(v => v.name.toUpperCase().trim())
				, [props.forms])

			const localStore = useLocalObservable(() => ({
				availableForms: forms.filter(
					(v) => !formPackageControlApi.formIsHidden(v.name),
				),
			}))

			const availableFormNames = useMemo(() =>
				localStore.availableForms.map(v => v.name.toUpperCase().trim())
				, [localStore.availableForms])

			const selectedIndex = useMemo(() =>
				formPackageControlApi.currentFormIndex
				, [formPackageControlApi.currentFormIndex])

			useEffect(() => {
				handleSelectForm(formPackageControlApi.currentFormIndex)
			}, [formPackageControlApi.currentFormIndex])

			useEffect(() => {
				const disposer = reaction(
					() => formPackageControlApi.hiddenForms.length,
					() => {
						runInAction(() => {
							localStore.availableForms = forms.filter(
								(v) => !formPackageControlApi.formIsHidden(v.name),
							)
						})
					},
					{
						fireImmediately: true,
					},
				)

				return () => disposer()
			}, [])

			const isFirstForm = useMemo(() => {
				for (let i = 0; i < selectedIndex; i++)
					if (!formPackageControlApi.formIsHidden(formNames[i]))
						return false
				return true
			}, [selectedIndex, localStore.availableForms])

			const isLastForm = useMemo(() => {
				// if no forms are hidden we can handle last form normally
				for (let i = selectedIndex + 1; i < forms.length; i++)
					if (!formPackageControlApi.formIsHidden(formNames[i]))
						return false
				return true
			}, [selectedIndex, localStore.availableForms])

			const isOnlyForm = useMemo(() => {
				return availableFormNames.length === 1
			}, [localStore.availableForms])

			const handleSelectForm = (index: number) => {
				if (selectedIndex === index) return

				// won't change tabs if wizard mode is active and form isn't valid
				if (!props.isCurrentFormValid(selectedIndex) && props.isWizardMode) {
					toastService.displayToast({
						message:
							'Current form must be valid before moving to a different form',
						area: 'formPackage',
						severity: 'error',
					})
					return
				}

				formPackageControlApi.currentFormIndex = props.onSelectForm(index)
			}

			return (
				<div className={styles.formSwapper}>
					<AppBar
						color="default"
						position="absolute"
						className={styles.appBar}
						ref={ref}
					>
						{!isOnlyForm && (
							<Tooltip
								title={isFirstForm ? '' : 'Previous Form'}
								className={styles.appBarLeft}
							>
								<IconButton
									onClick={() => handleSelectForm(selectedIndex - 1)}
									disabled={isFirstForm}
									color="primary"
									size="large"
								>
									<ChevronLeft />
								</IconButton>
							</Tooltip>
						)}

						<Button
							onClick={(evt: React.MouseEvent<HTMLButtonElement>) =>
								setAnchorEl(evt.currentTarget)
							}
							className={styles.appBarTitle}
						>
							{forms[selectedIndex].name}
						</Button>
						{!isOnlyForm && (
							<Tooltip
								title={isLastForm ? '' : 'Next Form'}
								className={styles.appBarRight}
							>
								<IconButton
									onClick={() => handleSelectForm(selectedIndex + 1)}
									disabled={isLastForm}
									color="primary"
									size="large"
								>
									<ChevronRight />
								</IconButton>
							</Tooltip>
						)}
						<Popover
							open={anchorEl !== null}
							onClose={() => setAnchorEl(null)}
							anchorEl={anchorEl}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'center',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'center',
							}}
						>
							<List>
								{localStore.availableForms.map((form, i) => (
									<ListItem
										key={form.id}
										divider={i !== localStore.availableForms.length - 1}
									>
										<ListItemButton
											onClick={() => {
												handleSelectForm(formNames.findIndex(v => v === availableFormNames[i]))
												setAnchorEl(null)
											}}
											selected={form.name.toUpperCase().trim() === formNames[selectedIndex]}
										>
											<ListItemText>{form.name}</ListItemText>
										</ListItemButton>
									</ListItem>
								))}
							</List>
						</Popover>
					</AppBar>
					<div className={styles.formViewerArrowContainer}>
						<div className={styles.leftArrowContainer}>
							{!isFirstForm && (
								<Tooltip title="Previous Form">
									<div>
										<IconButton
											onClick={() => {
												handleSelectForm(selectedIndex - 1)
											}}
											color="primary"
											size="large"
											className={styles.leftArrowButton}
										>
											<ChevronLeft fontSize="large" />
										</IconButton>
									</div>
								</Tooltip>
							)}
						</div>
						<div className={styles.rightArrowContainer}>
							{!isLastForm && (
								<Tooltip title="Next Form">
									<div>
										<IconButton
											className={styles.rightArrowButton}
											onClick={() => handleSelectForm(selectedIndex + 1)}
											color="primary"
											size="large"
										>
											<ChevronRight fontSize="large" />
										</IconButton>
									</div>
								</Tooltip>
							)}
						</div>
					</div>
				</div>
			)
		},
	),
)

const useStyles = makeStyles((theme: Theme) => ({
	appBar: {
		padding: theme.spacing(2, 0),
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-evenly',

		// stylings for a grid header are commented out

		// display: 'grid',
		// gridTemplateColumns: '3fr 1fr 3fr 1fr 3fr',
		// gridTemplateRows: '1fr',
		// gridRowGap: px(12),
	},

	appBarLeft: {
		// gridArea: '1 / 2 / 2 / 3',
		// zIndex: theme.zIndex.speedDial,
	},

	appBarTitle: {
		// gridArea: '1 / 3 / 2 / 4',
		// zIndex: theme.zIndex.speedDial,
	},

	appBarRight: {
		// gridArea: '1 / 4 / 2 / 5',
		// zIndex: theme.zIndex.speedDial,
	},

	formSwapper: {
		position: 'absolute',
		width: percent(100),
		height: percent(100),
		overflow: 'hidden',
	},

	formViewerArrowContainer: {
		position: 'absolute',
		width: percent(100),
		height: percent(100),
		[theme.breakpoints.down('sm')]: {
			display: 'none'
		}
	},

	leftArrowContainer: {
		position: 'absolute',
		display: 'flex',
		justifyContent: 'center',
		flexDirection: 'column',
		height: percent(100),
		left: 0,
		[`@media (min-width: ${theme.breakpoints.values.sm + Number(theme.spacing(11).slice(0, -2))}px)`]: {
			margin: theme.spacing(0, 2)
		},
	},

	leftArrowButton: {
		top: 0,
		left: 0,
		bottom: 0,
		zIndex: theme.zIndex.speedDial,
	},

	rightArrowContainer: {
		position: 'absolute',
		display: 'flex',
		justifyContent: 'center',
		flexDirection: 'column',
		height: percent(100),
		right: 0,
		[`@media (min-width: ${theme.breakpoints.values.sm + Number(theme.spacing(11).slice(0, -2))}px)`]: {
			margin: theme.spacing(0, 2)
		},
	},

	rightArrowButton: {
		top: 0,
		right: 0,
		bottom: 0,
		zIndex: theme.zIndex.speedDial,
	},
}))
